import { useMutation } from '@apollo/client/react';
import {
  Badge,
  Box,
  ButtonGroup,
  Heading,
  HStack,
  Button,
  Image,
  VStack,
  Text,
  Collapsible,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { toaster } from '#/components/ui/toaster';
import type {
  DocumentHistory,
  GetDocumentByPathQuery,
} from '#/graphql/generated';
import { UPSERT_DOCUMENT } from '#/graphql/mutations/upsert-document';
import { formatDocumentName } from '#/utils/format-document-name';
import { gravatarAvatarUrl, SYSTEM_GRAVATAR_HASH } from '#/utils/gravatar';

import {
  getHistoryEntryBody,
  historyRevisionHasSource,
  RevisionWordDiff,
} from './document-revision-diff';

/** Document branch only (parent passes null for DocumentNotFound). */
type DocumentFromQuery = Extract<
  GetDocumentByPathQuery['documentByPath'],
  { __typename: 'Document' }
> | null;

interface DocumentHistoryProps {
  /** Document from parent query; null when not found (DocumentNotFound). */
  document: DocumentFromQuery;
  /** Canonical path (document.path when document exists, else requested path from URL). */
  path: string;
  refetch: () => Promise<unknown>;
}

function DocumentHistoryContent({ document, path }: DocumentHistoryProps) {
  const navigate = useNavigate();

  const history = document?.documentHistory || [];

  if (!document) {
    return (
      <Box shadow="lg" borderRadius="lg" p={12}>
        <VStack gap={4}>
          <Text fontSize="lg" color="fg.muted" textAlign="center">
            Document not found
          </Text>
          <Text fontSize="sm" color="fg.subtle" textAlign="center">
            {`The document "${formatDocumentName(path).join(' > ')}" doesn't exist yet.`}
          </Text>
          <Button
            colorScheme="blue"
            onClick={() => navigate(`/documents/${path}`)}
          >
            Create Document
          </Button>
        </VStack>
      </Box>
    );
  }

  const formattedName = formatDocumentName(
    document.path ?? document.id ?? '',
  ).join(' > ');

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString();
  };

  return (
    <Box shadow="lg" borderRadius="lg" p={6}>
      <VStack gap={4} align="stretch">
        <HStack justify="space-between" align="center">
          <Heading size="lg" color="fg">
            {formattedName} - History
          </Heading>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/documents/${document.path}`)}
            >
              View
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => navigate(`/documents/${document.path}`)}
            >
              Edit
            </Button>
          </HStack>
        </HStack>

        {history.length === 0 ? (
          <Box py={8} textAlign="center">
            <Text color="fg.muted">
              No history available for this document.
            </Text>
          </Box>
        ) : (
          <VStack gap={2} align="stretch">
            {history.map((historyEntry, index) => {
              if (!historyEntry) return null;
              const previousEntry = history[index + 1] ?? null;
              return (
                <HistoryRevision
                  key={historyEntry.id}
                  historyEntry={historyEntry as DocumentHistory}
                  previousEntry={
                    previousEntry ? (previousEntry as DocumentHistory) : null
                  }
                  formatDate={formatDate}
                  currentDocumentValue={document.value || ''}
                  documentPath={path}
                  documentId={document.id || ''}
                />
              );
            })}
          </VStack>
        )}
      </VStack>
    </Box>
  );
}

type CompareMode = 'vsCurrent' | 'vsPrevious';

interface HistoryRevisionProps {
  historyEntry: DocumentHistory;
  /** Next item in history array (older snapshot); null for oldest revision. */
  previousEntry: DocumentHistory | null;
  formatDate: (date: Date | string) => string;
  currentDocumentValue: string;
  documentPath: string;
  documentId: string;
}

function RevisionChangedByAvatar({ gravatarHash }: { gravatarHash: string }) {
  const isSystemBranding = gravatarHash === SYSTEM_GRAVATAR_HASH;
  return (
    <Image
      src={isSystemBranding ? '/icon.svg' : gravatarAvatarUrl(gravatarHash, 40)}
      alt=""
      aria-hidden
      boxSize="20px"
      borderRadius="full"
      flexShrink={0}
      objectFit={isSystemBranding ? 'contain' : 'cover'}
      p={isSystemBranding ? '2px' : undefined}
      bg={
        isSystemBranding ? 'var(--background-base-highlight-color)' : undefined
      }
    />
  );
}

function HistoryRevision({
  historyEntry,
  previousEntry,
  formatDate,
  currentDocumentValue,
  documentPath,
  documentId,
}: HistoryRevisionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [compareMode, setCompareMode] = useState<CompareMode>('vsCurrent');
  const navigate = useNavigate();
  const [upsertDocument, { loading: reverting }] = useMutation(UPSERT_DOCUMENT);

  const aiAssisted = historyRevisionHasSource(historyEntry);
  const changedBy = historyEntry.changedBy;

  const revisionBody = getHistoryEntryBody(historyEntry);
  const canComparePrevious = previousEntry != null;
  const effectiveMode: CompareMode =
    compareMode === 'vsPrevious' && canComparePrevious
      ? 'vsPrevious'
      : 'vsCurrent';

  const diffFromTo =
    effectiveMode === 'vsPrevious' && previousEntry
      ? {
          fromValue: getHistoryEntryBody(previousEntry),
          toValue: revisionBody,
          fromLabel: formatDate(previousEntry.createdAt),
          toLabel: formatDate(historyEntry.createdAt),
        }
      : {
          fromValue: currentDocumentValue,
          toValue: revisionBody,
          fromLabel: 'Current',
          toLabel: formatDate(historyEntry.createdAt),
        };

  const handleRevert = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const historyValue = getHistoryEntryBody(historyEntry);

    try {
      const { data } = await upsertDocument({
        variables: {
          input: {
            id: documentId,
            path: documentPath,
            value: historyValue,
          },
        },
      });

      if (data?.upsertDocument?.document) {
        toaster.create({
          title: 'Success',
          description: 'Document reverted to this revision',
          type: 'success',
        });
        // Navigate to view the updated document
        navigate(`/documents/${documentPath}`);
      } else {
        throw new Error('Failed to revert document');
      }
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: `Failed to revert document: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        type: 'error',
      });
    }
  };

  return (
    <Box
      border="1px solid"
      borderColor="border"
      borderRadius="md"
      overflow="hidden"
    >
      <Collapsible.Root
        lazyMount
        open={isOpen}
        onOpenChange={(e) => setIsOpen(e.open)}
      >
        <HStack gap={2} align="stretch" width="100%">
          <Collapsible.Trigger asChild style={{ flex: 1 }}>
            <Button
              variant="ghost"
              width="100%"
              justifyContent="space-between"
              p={4}
              h="auto"
              _hover={{ bg: 'bg.subtle' }}
            >
              <HStack
                gap={3}
                flex={1}
                justify="space-between"
                align="center"
                flexWrap="wrap"
              >
                <HStack gap={2} align="center">
                  {isOpen ? (
                    <FiChevronDown size={16} />
                  ) : (
                    <FiChevronRight size={16} />
                  )}
                  <Text fontSize="sm" fontWeight="semibold" color="fg">
                    Revision
                  </Text>
                  {aiAssisted ? (
                    <Badge colorPalette="purple" size="sm">
                      AI-assisted
                    </Badge>
                  ) : null}
                </HStack>
                <HStack gap={2} align="center" minW={0}>
                  {changedBy?.gravatarHash ? (
                    <RevisionChangedByAvatar
                      gravatarHash={changedBy.gravatarHash}
                    />
                  ) : null}
                  <Text fontSize="xs" color="fg.muted" truncate>
                    {changedBy?.displayName ?? '—'}
                  </Text>
                </HStack>
                <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                  {formatDate(historyEntry.createdAt)}
                </Text>
              </HStack>
            </Button>
          </Collapsible.Trigger>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            px={2}
            borderLeft="1px solid"
            borderColor="border"
          >
            <Button
              size="xs"
              variant="outline"
              colorScheme="orange"
              onClick={handleRevert}
              loading={reverting}
              loadingText="Reverting..."
            >
              Revert
            </Button>
          </Box>
        </HStack>
        <Collapsible.Content>
          <Box
            px={4}
            pb={4}
            pt={2}
            width="100%"
            borderTop="1px solid"
            borderColor="border"
          >
            <VStack gap={3} align="stretch">
              <HStack gap={3} align="center" flexWrap="wrap">
                <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                  Compare
                </Text>
                <ButtonGroup size="xs" variant="outline" attached>
                  <Button
                    variant={
                      effectiveMode === 'vsCurrent' ? 'solid' : 'outline'
                    }
                    onClick={() => setCompareMode('vsCurrent')}
                  >
                    Current
                  </Button>
                  <Button
                    variant={
                      effectiveMode === 'vsPrevious' ? 'solid' : 'outline'
                    }
                    disabled={!canComparePrevious}
                    title={
                      canComparePrevious
                        ? undefined
                        : 'No earlier revision in history'
                    }
                    onClick={() => {
                      if (canComparePrevious) setCompareMode('vsPrevious');
                    }}
                  >
                    This change
                  </Button>
                </ButtonGroup>
                {!canComparePrevious ? (
                  <Text fontSize="xs" color="fg.subtle">
                    No earlier revision to diff this edit against.
                  </Text>
                ) : null}
              </HStack>
              <RevisionWordDiff
                fromValue={diffFromTo.fromValue}
                toValue={diffFromTo.toValue}
                fromLabel={diffFromTo.fromLabel}
                toLabel={diffFromTo.toLabel}
              />
            </VStack>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}

export function DocumentHistory({
  document,
  path,
  refetch,
}: DocumentHistoryProps) {
  return (
    <DocumentHistoryContent document={document} path={path} refetch={refetch} />
  );
}
